import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Box, Grid, Switch,
  FormControlLabel, Avatar, Chip, Divider, useTheme, useMediaQuery, Card, CardContent
} from '@mui/material';
import {
  Edit, Delete, Add, Article, Campaign, PushPin, NotificationsActive,
  PhotoCamera, Close, Visibility
} from '@mui/icons-material';
import deputyService from '../../utils/deputyService';
import api from '../../utils/api';
import { snackbar, successMessage } from '../../utils/showMessage';
import RichTextEditor from '../../components/RichTextEditor';
import moment from 'moment-jalaali';

// Helper function for date display
moment.loadPersian({ usePersianDigits: false });

const formatJalaliDate = (gDate) => {
  if (!gDate) return '';
  const parsed = moment(gDate);
  if (!parsed.isValid()) return '';
  return parsed.format('jYYYY/jMM/jDD');
};

const parseJalaliToGregorian = (value) => {
  if (!value) return '';
  const normalized = value.replace(/[^0-9/-]/g, '').replace(/-/g, '/');
  const candidates = ['jYYYY/jMM/jDD', 'jYYYY/jM/jD', 'YYYY/MM/DD', 'YYYY-MM-DD'];
  for (const format of candidates) {
    const parsed = moment(normalized, format, true);
    if (parsed.isValid()) {
      return parsed.format('YYYY-MM-DD');
    }
  }
  return '';
};

const textFieldStyle = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fff',
    '& fieldset': { borderColor: 'rgba(0,0,0,0.1)' }
  }
};

const DeputyManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [deputy, setDeputy] = useState([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    contact1:'',
    contact2:'',
    summary: '',
    about: '',
    image: '',
    gallery: [],
    isActive: true,
    isPinned: false,
    sendNotification: false,
    order: 0,
    publishDate: new Date().toISOString().split('T')[0],
    publishDateJalali: formatJalaliDate(new Date().toISOString()),
    tags: []
  });

  const loadDeputy = useCallback(async () => {
    try {
      setLoading(true);
      const data = await deputyService.getAll(null, true);
      setDeputy(data || []);
    } catch (err) {
      console.error('Error fetching deputy:', err);
      snackbar('خطا در بارگذاری معاونت');
      setDeputy([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDeputy();
  }, [loadDeputy]);

  const handleOpen = (item = null) => {
    if (item) {
      setCurrent(item);
      setFormData({
        title: item.title || '',
        content: item.content || '',
        summary: item.summary || '',
        type: item.type || 'deputy',
        image: item.image || '',
        gallery: item.gallery || [],
        isActive: item.isActive !== undefined ? item.isActive : true,
        isPinned: item.isPinned || false,
        sendNotification: false,
        order: item.order || 0,
        publishDate: item.publishDate || '',
        publishDateJalali: formatJalaliDate(item.publishDate || ''),
        tags: item.tags || []
      });
    } else {
      setCurrent(null);
      setFormData({
        title: '',
        content: '',
        summary: '',
        type: 'deputy',
        image: '',
        gallery: [],
        isActive: true,
        isPinned: false,
        sendNotification: false,
        order: 0,
        publishDate: '',
        publishDateJalali: '',
        tags: []
      });
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handlePublishDateInput = (value) => {
    const normalized = value.replace(/[^0-9/-]/g, '').replace(/-/g, '/');
    const gregorian = parseJalaliToGregorian(normalized);
    setFormData({
      ...formData,
      publishDateJalali: normalized,
      publishDate: gregorian
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'publishDate') {
      handlePublishDateInput(value);
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

 

  const getImageUrl = useCallback((img) => {
    if (!img) return '';
    if (img.startsWith('http')) return img;
    return `${process.env.REACT_APP_FILE_BASE_URL}/uploads/documents/${img}`;
  }, []);

  const handleEditorUpload = useCallback(async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const response = await api.post('/welfare/upload-image', fd);
    return getImageUrl(response.data.filename);
  }, [getImageUrl]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fd = new FormData();
    fd.append('file', file);

    try {
      const response = await api.post('/welfare/upload-image', fd);
      const filename = response.data.filename;
      setFormData({ ...formData, image: filename });
      successMessage('تصویر با موفقیت آپلود شد');
    } catch (err) {
      console.error(err);
      snackbar('خطا در آپلود تصویر');
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setLoading(true);
    const uploadedImages = [];

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/welfare/upload-image', formData);
        uploadedImages.push(response.data.filename);
      }
      setFormData({ ...formData, gallery: [...(formData.gallery || []), ...uploadedImages] });
      successMessage('تصاویر به گالری اضافه شدند');
    } catch (error) {
      console.error('Error uploading gallery images:', error);
      snackbar('خطا در آپلود تصاویر گالری');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveGalleryImage = (index) => {
    const newGallery = [...(formData.gallery || [])];
    newGallery.splice(index, 1);
    setFormData({ ...formData, gallery: newGallery });
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        tags: typeof formData.tags === 'string' ? formData.tags.split(',').map(t => t.trim()) : formData.tags
      };

      if (current) {
        const { sendNotification, ...updatePayload } = payload;
        await deputyService.update(current._id, updatePayload);
        successMessage('معاونت با موفقیت ویرایش شد');
      } else {
        await deputyService.create(payload);
        successMessage('معاونت با موفقیت ایجاد شد');
      }
      handleClose();
      loadDeputy();
    } catch (err) {
      console.error(err);
      snackbar(err?.response?.data?.message || 'خطا در ذخیره');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این معاونت اطمینان دارید؟')) return;
    try {
      await deputyService.delete(id);
      successMessage('معاونت با موفقیت حذف شد');
      loadDeputy();
    } catch (err) {
      console.error(err);
      snackbar('خطا در حذف');
    }
  };

  const handleResendNotification = async (id) => {
    if (!window.confirm('ارسال مجدد نوتیفیکیشن برای این معاونت انجام شود؟')) return;
    try {
      await api.post(`/deputy/${id}/send-notification`);
      successMessage('نوتیفیکیشن ارسال شد');
    } catch (err) {
      console.error(err);
      snackbar(err?.response?.data?.message || 'خطا در ارسال نوتیفیکیشن');
    }
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 3, mt: isMobile ? 3 : 5 }}>
      <Box sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: 2,
        mb: 3,
        mt: isMobile ? 6 : 5
      }}>
        <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">مدیریت معاونت ها</Typography>
        {/* <Button 
          variant="contained" 
          startIcon={<Add />} 
          onClick={() => handleOpen()}
          fullWidth={isMobile}
          sx={{ borderRadius: '12px', py: isMobile ? 1.5 : 1 }}
        >
          معاونت جدید
        </Button> */}
      </Box>

      {isMobile ? (
        <Grid container spacing={2}>
          {deputy.map((item) => (
            <Grid item xs={12} key={item._id}>
              <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #eee', bgcolor: '#fafafa' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                    <Avatar
                      src={getImageUrl(item.image)}
                      variant="rounded"
                      sx={{ width: 80, height: 80, borderRadius: 3 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{ maxWidth: '150px' }}>
                          {item.title}
                        </Typography>
                        <Chip
                          label={item.isActive ? 'فعال' : 'غیرفعال'}
                          size="small"
                          color={item.isActive ? 'success' : 'default'}
                          sx={{ borderRadius: '8px', height: 20, fontSize: '10px' }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, my: 1 }}>
                        {item.type === 'announcement' ? (
                          <Chip icon={<Campaign sx={{ fontSize: '14px !important' }} />} label="اطلاعیه" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '10px' }} />
                        ) : (
                          <Chip icon={<Article sx={{ fontSize: '14px !important' }} />} label="معاونت" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '10px' }} />
                        )}
                        {item.isPinned && <PushPin sx={{ fontSize: 16, color: 'orange' }} />}
                      </Box>

                      <Typography variant="caption" color="textSecondary" display="block">
                        🔢 ردیف: {item.order ?? 0} | 👁️ {item.viewCount || 0} بازدید | 📅 {formatJalaliDate(item.publishDate)}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 1.5 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <IconButton size="small" onClick={() => handleOpen(item)} sx={{ bgcolor: '#fff', border: '1px solid #eee' }}>
                      <Edit fontSize="small" color="primary" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleResendNotification(item._id)} sx={{ bgcolor: '#fff', border: '1px solid #eee' }}>
                      <NotificationsActive fontSize="small" color="info" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(item._id)} sx={{ bgcolor: '#fff', border: '1px solid #eee' }}>
                      <Delete fontSize="small" color="error" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {deputy.length === 0 && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 4, borderStyle: 'dashed' }}>
                <Typography variant="body2" color="textSecondary">معاونتی یافت نشد</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 4, border: '1px solid #eee' }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell align="right">ردیف</TableCell>
                <TableCell align="right">تصویر</TableCell>
                <TableCell align="right">عنوان</TableCell>
                {!isTablet && <TableCell align="right">بازدید</TableCell>}
                <TableCell align="center">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deputy.map((item) => (
                <TableRow key={item._id} hover>
                  <TableCell align="right" sx={{ fontWeight: 600, width: 60 }}>{item.order ?? 0}</TableCell>
                  <TableCell align="right">
                    <Avatar src={getImageUrl(item.image)} variant="rounded" sx={{ width: 50, height: 50, borderRadius: 2 }} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500 }}>{item.title}</TableCell>



                  {!isTablet && <TableCell align="right">{item.viewCount || 0}</TableCell>}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => handleOpen(item)} title="ویرایش">
                        <Edit fontSize="small" color="primary" />
                      </IconButton>
                      {/* <IconButton size="small" onClick={() => handleResendNotification(item._id)} title="ارسال نوتیفیکیشن">
                        <NotificationsActive fontSize="small" color="info" />
                      </IconButton> */}
                      {/* <IconButton size="small" onClick={() => handleDelete(item._id)} title="حذف">
                        <Delete fontSize="small" color="error" />
                      </IconButton> */}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 4 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {current ? 'ویرایش معاونت' : 'ایجاد معاونت جدید'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="عنوان"
                name="title"
                value={formData.title}
                onChange={handleChange}
                sx={textFieldStyle}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="ردیف"
                name="order"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) || 0 })}
                sx={textFieldStyle}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="نام"
                name="name"
                value={formData.name}
                onChange={handleChange}
                sx={textFieldStyle}
                required
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="تلفن"
                name="contact1"
                value={formData.contact1}
                onChange={handleChange}
                sx={textFieldStyle}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="تماس 2"
                name="contact2"
                value={formData.contact2}
                onChange={handleChange}
                sx={textFieldStyle}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <RichTextEditor
                value={formData.about}
                onChange={(value) => setFormData({ ...formData, about: value })}
                label="درباره"
                onUpload={handleEditorUpload}
              />
            </Grid>

            <Grid item xs={12}>
              <RichTextEditor
                value={formData.summary}
                onChange={(value) => setFormData({ ...formData, summary: value })}
                label="شرح وظایف"
                onUpload={handleEditorUpload}
              />
            </Grid>
            <Grid item xs={12}>
              <RichTextEditor
                value={formData.content}
                onChange={(value) => setFormData({ ...formData, content: value })}
                label="گزارش عملکرد"
                onUpload={handleEditorUpload}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ borderRadius: '12px', borderStyle: 'dashed', py: 1.5 }}
              >
                <PhotoCamera sx={{ mr: 1 }} />
                آپلود تصویر شاخص
                <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
              </Button>
              {formData.image && (
                <Box mt={2} position="relative">
                  <img src={getImageUrl(formData.image)} alt="Preview" style={{ width: '100%', maxHeight: '180px', objectFit: 'cover', borderRadius: '12px', border: '1px solid #eee' }} />
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: '#fff' } }}
                    onClick={() => setFormData({ ...formData, image: '' })}
                  >
                    <Close fontSize="small" color="error" />
                  </IconButton>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} sx={{ borderRadius: '8px' }}>انصراف</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ borderRadius: '8px', px: 4 }}>
            ذخیره تغییرات
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};


export default DeputyManagement;
